import apiClient from './api';
import type { User, UserUpdate, LoginRequest, RegisterRequest, TokenResponse } from '@/types/auth';

// ── Invitation token types ────────────────────────────────────────────────────

export interface InvitationPreview {
  email: string;
  intended_role: 'coach' | 'athlete';
  expires_at: string;
  valid: boolean;
  /** Optional message from the backend explaining why the invitation is invalid */
  message?: string;
  /**
   * Machine-readable error code when valid=false.
   * - "not_found"    — token does not exist
   * - "already_used" — redeemed by a real user (used_by_user_id is set)
   * - "revoked"      — administratively cancelled (used_by_user_id IS NULL)
   * - "expired"      — past expires_at
   */
  error?: 'not_found' | 'already_used' | 'revoked' | 'expired' | string;
}

export interface InvitationRedeemRequest {
  display_name: string;
  password: string;
  turnstile_token?: string;
  /** User-chosen role at registration; overrides invitation's intended_role when set. */
  role?: 'athlete' | 'coach';
}

const AUTH_TOKEN_KEY = 'authToken';

export function getStoredToken(): string | null {
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function storeTokens(tokens: TokenResponse): void {
  try {
    // Only store the short-lived access token. The refresh token now lives
    // exclusively in an httpOnly SameSite=Strict cookie set by the backend
    // and is therefore invisible to JS (XSS-safe).
    localStorage.setItem(AUTH_TOKEN_KEY, tokens.access_token);
  } catch {
    // localStorage may not be available
  }
}

export function clearTokens(): void {
  try {
    // Only the access token lives in localStorage; the refresh token cookie
    // is cleared by the backend on logout via delete_cookie.
    localStorage.removeItem(AUTH_TOKEN_KEY);
  } catch {
    // localStorage may not be available
  }
}

export async function login(data: LoginRequest): Promise<TokenResponse> {
  const response = await apiClient.post<TokenResponse>('/auth/login', data);
  return response.data;
}

export async function register(data: RegisterRequest): Promise<TokenResponse> {
  const response = await apiClient.post<TokenResponse>('/auth/register', data);
  return response.data;
}

export async function refreshToken(): Promise<TokenResponse> {
  // Send an empty body — the refresh token is supplied automatically via the
  // httpOnly cookie (withCredentials ensures the browser attaches it).
  const response = await apiClient.post<TokenResponse>('/auth/refresh', {}, {
    withCredentials: true,
  });
  return response.data;
}

export async function getMe(): Promise<User> {
  const response = await apiClient.get<User>('/auth/me');
  return response.data;
}

export async function updateMe(data: UserUpdate): Promise<User> {
  const response = await apiClient.patch<User>('/auth/me', data);
  return response.data;
}

export async function logout(): Promise<void> {
  try {
    // POST to /api/auth/logout so the backend can clear the httpOnly refresh
    // token cookie via delete_cookie. Must use withCredentials so the browser
    // sends the cookie with the request.
    await apiClient.post('/auth/logout', {}, { withCredentials: true });
  } catch {
    // Network errors or 5xx from the server: still clean up local state and
    // redirect — we must not trap the user in a broken session.
  } finally {
    clearTokens();
  }
}

// ── Invitation API ────────────────────────────────────────────────────────────

/**
 * Preview an invitation token — no auth required.
 * Returns the invitation metadata and whether it is still valid.
 */
export async function previewInvitation(token: string): Promise<InvitationPreview> {
  const response = await apiClient.get<InvitationPreview>(
    `/registration-invitations/${encodeURIComponent(token)}`
  );
  return response.data;
}

/**
 * Redeem an invitation token to create a new user account.
 * Returns access + refresh tokens on success.
 */
export async function redeemInvitation(
  token: string,
  data: InvitationRedeemRequest
): Promise<TokenResponse> {
  const response = await apiClient.post<TokenResponse>(
    `/registration-invitations/${encodeURIComponent(token)}/redeem`,
    data
  );
  return response.data;
}

// ── Forgot / Reset Password API ───────────────────────────────────────────────

export interface PasswordResetTokenPreview {
  valid: boolean;
  /** Masked email shown when valid=true, e.g. "b***@example.com" */
  email?: string;
  /** Machine-readable error code when valid=false */
  error?: 'expired' | 'used' | 'not_found' | string;
  /** Human-readable error message when valid=false */
  message?: string;
}

/**
 * Request a password-reset email.
 * Always returns the same success message regardless of whether the email exists
 * (prevents email enumeration).
 */
export async function requestPasswordReset(email: string): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>('/auth/forgot-password', { email });
  return response.data;
}

/**
 * Validate a password reset token without consuming it.
 * Used by the ResetPasswordPage on mount to show the form or an error screen.
 */
export async function validatePasswordResetToken(
  token: string
): Promise<PasswordResetTokenPreview> {
  const response = await apiClient.get<PasswordResetTokenPreview>(
    `/auth/reset-password/${encodeURIComponent(token)}`
  );
  return response.data;
}

/**
 * Consume a password reset token and set a new password.
 * On success, all existing sessions are invalidated (token_version bumped).
 */
export async function resetPassword(
  token: string,
  newPassword: string
): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>('/auth/reset-password', {
    token,
    new_password: newPassword,
  });
  return response.data;
}

// ── Email Change (two-step verification flow) ─────────────────────────────────

/**
 * Typed error codes returned by POST /api/auth/email-change/request.
 * Matches the exact `error` strings in the backend handler.
 */
export type EmailChangeRequestErrorCode =
  | 'same_email'    // 400 — new email is identical to current email
  | 'email_taken'   // 409 — another account already uses this address
  | 'rate_limited'; // 429 — exceeded 5 requests/min

/**
 * Typed error codes returned by POST /api/auth/email-change/confirm.
 */
export type EmailChangeConfirmErrorCode =
  | 'invalid_or_expired_token' // 400 — token not found, already used, or expired
  | 'email_taken';             // 409 — address claimed by another account between request + confirm

/**
 * Typed error raised when attempting to change email directly via PATCH /auth/me
 * (the old flow). The backend now returns 400 with this code to direct callers
 * to the two-step verification flow.
 */
export type EmailChangePatchBlockedCode = 'email_change_requires_verification';

/**
 * Request an email-change verification link.
 *
 * POST /api/auth/email-change/request — auth required.
 * On success: returns { message } — the email is NOT yet changed.
 *
 * Throws an AxiosError on failure. Callers should branch on the `error` code
 * using getErrorCode() from @/utils/errors:
 *   - "same_email"   → "That's already your email address."
 *   - "email_taken"  → "That email address is already in use."
 *   - "rate_limited" → "Too many requests. Please wait a moment."
 *   - 422            → "Please enter a valid email address."
 */
export async function requestEmailChange(newEmail: string): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>('/auth/email-change/request', {
    new_email: newEmail,
  });
  return response.data;
}

/**
 * Confirm an email change by consuming the one-time token from the link.
 *
 * POST /api/auth/email-change/confirm — NO auth (token is the credential).
 * On success: the email is changed and all sessions are invalidated by the
 * backend (token_version bump). The caller must clear local auth state and
 * redirect to /login.
 *
 * Throws an AxiosError on failure. Callers should branch on the `error` code:
 *   - "invalid_or_expired_token" → "This link is invalid or has expired."
 *   - "email_taken"              → "That email is already taken."
 */
export async function confirmEmailChange(token: string): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>('/auth/email-change/confirm', {
    token,
  });
  return response.data;
}

// ── Google OAuth (Option A — existing users only) ─────────────────────────────

/**
 * Typed error codes returned by POST /auth/google/callback.
 * Match the exact `error` strings in the backend handler (auth_routes.py).
 */
export type GoogleCallbackErrorCode =
  | 'oauth_not_configured'    // 503 — server not provisioned yet
  | 'invalid_google_token'    // 401 — token expired / malformed / signature invalid
  | 'google_email_not_verified' // 401 — Google account email not verified
  | 'account_not_found'       // 401 — no PointeTracer account matches this Google identity
  | 'account_inactive'        // 401 — account deactivated
  | 'google_account_mismatch' // 409 — account already linked to a different Google account
  | 'google_account_conflict'; // 409 — concurrent race: this Google sub already linked elsewhere

/**
 * Authenticate an existing PointeTracer user via a Google ID token.
 *
 * POSTs to POST /api/auth/google/callback with { credential }.
 * On success returns the same TokenResponse as /auth/login.
 * On failure re-throws the AxiosError so the caller can branch on
 * the `error` code via getErrorCode() / getGoogleErrorCode().
 *
 * Also calls storeTokens() on success — mirrors the login() pattern exactly.
 */
export async function loginWithGoogle(credential: string): Promise<TokenResponse> {
  const response = await apiClient.post<TokenResponse>('/auth/google/callback', { credential });
  storeTokens(response.data);
  return response.data;
}
